import { useState, useEffect, useMemo, useRef, Fragment } from 'react'
import { useParams, useNavigate, useSearchParams, useLocation, Link as RouterLink } from 'react-router-dom'
import { atom, useAtom } from 'jotai'
import { useWebSocket } from '../hooks/useWebSocket'
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Link,
  Skeleton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Add as AddIcon,
  ArrowDropDown as ArrowDropDownIcon,
  OpenInNew as OpenInNewIcon,
  BusinessCenter as BusinessCenterIcon,
  Domain as DomainIcon,
  PersonSearch as PersonSearchIcon,
  TravelExplore as TravelExploreIcon,
  Visibility as VisibilityIcon,
  UploadFile as UploadFileIcon,
  FilterAltOutlined as FilterAltOutlinedIcon,
} from '@mui/icons-material'
import EmptyState from '../components/common/EmptyState'
import ErrorState from '../components/common/ErrorState'
import TableSkeleton from '../components/common/table/TableSkeleton'
import { ExpanderField, ExpanderHeaderCell, ExpanderRow, ExpanderToggleCell } from '../components/common/table/RowExpander'
import JobApplicationCard from '../components/job-applications/JobApplicationCard'
import PageHelpButton from '../components/PageHelpButton'
import {
  jobOpportunitiesAtom,
  jobOpportunitiesLoadingAtom,
  JobOpportunity,
  APPLY_DECISION_LABELS,
  APPLY_DECISION_COLORS,
  COMPANY_TIER_LABELS,
  COMPANY_TIER_CHIP_COLOR,
  COMPANY_TIERS,
  resolveCompanyTier,
  APPLY_DECISIONS,
  fitScoreColor,
} from '../state/jobOpportunities'
import { userAtom } from '../state/auth'
import { rememberedClientIdAtom, isJobSearchEligible } from '../state/clients'
import { isExecutiveClient, EXEC_TIER_HINT, hasWildcardOutreach, WILDCARD_HINT } from '../utils/clientTier'
import { interviewStageDefinitionsAtom, interviewStagesLoadingAtom } from '../state/interviews'
import {
  jobOpportunitiesApi,
  clientsApi,
  interviewsApi,
  Client,
  Interview,
  InterviewStageDefinition,
} from '../services/api'
import InterviewDetailDialog from '../components/interview-prep/InterviewDetailDialog'
import AddJobDialog from '../components/job-applications/AddJobDialog'
import HiddenOpportunitiesTab from '../components/job-applications/HiddenOpportunitiesTab'
import LogHiddenOpportunityDialog from '../components/job-applications/LogHiddenOpportunityDialog'
import ImportJobApplicationsDialog from '../components/job-applications/ImportJobApplicationsDialog'
import JobDetailDialog from '../components/job-applications/JobDetailDialog'
import InactivityChip from '../components/job-applications/InactivityChip'
import ExecutiveSearchTab from '../components/job-applications/ExecutiveSearchTab'
import TargetCompaniesTab from '../components/job-applications/TargetCompaniesTab'
import WarmContactsCell from '../components/job-applications/WarmContactsCell'
import {
  JOB_TABS as TABS,
  jobTabKey,
  TERMINAL_JOB_TABS,
  JobTabKey as TabKey,
} from '../components/job-applications/jobTabRouting'
import { useNotification } from '../hooks/useNotification'
import { useClientNetwork } from '../hooks/useClientNetwork'
import { isAdminOrManager } from '../utils/roles'
import { formatJobDate } from '../utils/dateRange'
import { emptyTabDescription } from '../utils/emptyTabDescription'
import { ensureHttpProtocol } from '../utils/validation'
import { getEnhancedTableStyles } from '../utils/tableStyles'
import { getCompactTableSx, getTightDesktopTableSx } from '../utils/compactTableStyles'
import SortableTableHeader, { SortOrder } from '../components/common/table/SortableTableHeader'
import FilterBar, { FilterBarFilter } from '../components/common/table/FilterBar'
import { useTablePagination } from '../components/common/table/useTablePagination'
import TablePaginationBar from '../components/common/table/TablePaginationBar'
import ClientPicker from '../components/common/ClientPicker'

const TARGET_COMPANIES_HINT = 'Select a client to see their target companies'
const EXEC_SEARCH_HINT = 'Select a client to see their recruiters'
const WILDCARD_SELECT_HINT = 'Select a client to see their wild card outreach'

// The per-client tabs that sit after the pipeline tabs and show an entity list
// rather than job rows.
type EntityTab = 'execsearch' | 'targetcompanies' | 'wildcard'
const isEntityTab = (t: string): t is EntityTab =>
  t === 'execsearch' || t === 'targetcompanies' || t === 'wildcard'
const DEFAULT_TAB: TabKey = 'pending_decision'
const isKnownTab = (t: string | null): t is TabKey | EntityTab =>
  !!t && (isEntityTab(t) || TABS.some((tab) => tab.key === t))

// MWA-251 — fixed, locked tab set (states intentionally not admin-configurable
// for this flow). Each job lands in exactly ONE tab via jobTabKey() (shared with
// the portal page — see jobTabRouting.ts). The two entity tabs (Target
// Companies, Executive Search) render their own components.

// MWA-338 — the switcher's options have to outlive a remount. Picking a client
// routes from /opportunities to /clients/:id/opportunities, which swaps
// one Route element for another, so this page unmounts and useState goes with
// it. Measured on dev: the picker sat empty for ~680ms of the refetch, with no
// options to click and no name for a board the route had already scoped, which
// is what read as "the filter doesn't work". A module-level atom survives the
// remount, so the second visit paints from what we already have and the refetch
// happens behind it. Deliberately NOT the shared clientsAtom — ClientPipeline
// writes that one, and this page must not inherit another page's idea of the
// list.
const jobSearchClientsAtom = atom<Client[]>([])

export default function JobApplicationsTracker() {
  const [jobs, setJobs] = useAtom(jobOpportunitiesAtom)
  const [loading, setLoading] = useAtom(jobOpportunitiesLoadingAtom)
  // loadJobs() raises `loading` on every read, including re-reads (an import
  // finishing, for one). Only the first one is allowed to replace the page with
  // a spinner: doing it later unmounts the interview dialog with the rest of
  // the tree, killing its WebSocket subscription and any in-flight
  // guide-generation fallback timer.
  const [firstJobsReadDone, setFirstJobsReadDone] = useState(false)
  const [user] = useAtom(userAtom)
  const [error, setError] = useState<string | null>(null)
  const [clients, setClients] = useAtom(jobSearchClientsAtom)
  // Only a cold read blocks: arriving with a list already in hand refreshes
  // behind the picker rather than emptying it.
  const [clientsLoading, setClientsLoading] = useState(clients.length === 0)
  const [clientsError, setClientsError] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  // MWA-362 — the tab lives in ?tab= so Back steps through tab changes and a
  // return visit lands where you left. Entity tabs (recruiters / target
  // companies) live outside the jobs TabKey union. A choice pushes history;
  // corrections the page makes on its own replace, so Back never lands on a
  // tab the page would immediately move you off.
  const tabParam = searchParams.get('tab')
  const activeTab: TabKey | EntityTab = isKnownTab(tabParam) ? tabParam : DEFAULT_TAB
  const setActiveTab = (tab: TabKey | EntityTab, { replace = false }: { replace?: boolean } = {}) => {
    const next = new URLSearchParams(searchParams)
    if (tab === DEFAULT_TAB) next.delete('tab')
    else next.set('tab', tab)
    setSearchParams(next, { replace })
  }
  // MWA-252 — the whole staff flow is scoped to ONE selected client. The client
  // now lives in the route (/clients/:id/opportunities) rather than local
  // state, so the URL is shareable and survives navigation.
  const { id: routeClientId } = useParams<{ id: string }>()
  const selectedClientId = routeClientId ?? null
  const navigate = useNavigate()
  const [, setRememberedClientId] = useAtom(rememberedClientIdAtom)
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [logHiddenOpen, setLogHiddenOpen] = useState(false)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  // The header Add menu opens the Target Companies / Executive Search add dialogs
  // (the per-tab Add buttons were removed in favor of this single menu). Each flag
  // is a one-shot trigger: the tab opens its dialog, then calls back to clear it
  // so it can't re-fire when the tab remounts on later navigation.
  const [tcAutoAdd, setTcAutoAdd] = useState(false)
  const [esAutoAdd, setEsAutoAdd] = useState(false)
  const [wcAutoAdd, setWcAutoAdd] = useState(false)
  const [selectedJob, setSelectedJob] = useState<JobOpportunity | null>(null)
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  // The applications table is the single surface for the whole
  // application-to-offer lifecycle, so the interview behind a job opens here
  // rather than on a separate page. Interviews are loaded for the interview
  // stage column and to resolve the ?interviewId= deep link.
  const [interviews, setInterviews] = useState<Interview[]>([])
  const [interviewsLoading, setInterviewsLoading] = useState(true)
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null)
  const [interviewDialogOpen, setInterviewDialogOpen] = useState(false)
  const [interviewStages] = useAtom(interviewStageDefinitionsAtom)
  const [interviewStagesLoading] = useAtom(interviewStagesLoadingAtom)
  const requestedInterviewId = searchParams.get('interviewId')
  // MWA-361 — ?job=<id> opens that opportunity's detail dialog (the dashboard's
  // Pending Applications rows link here).
  const requestedJobId = searchParams.get('job')
  const { showSuccess, showError } = useNotification()

  const isManager = isAdminOrManager(user) || user?.role === 'client_success'

  // MWA-390 — after the socket comes back, reload what this page shows. Read through
  // a ref so the reload uses the client currently in the route, not the first render's.
  const reloadRef = useRef<() => void>(() => {})
  useEffect(() => {
    reloadRef.current = () => {
      loadJobs()
      loadInterviews()
    }
  })

  // WebSocket handlers — keep the live list in sync (create/update/delete).
  const wsHandlers = useMemo(
    () => ({
      job_opportunity_created: (message: { data: unknown }) => {
        const newJob = message.data as JobOpportunity
        setJobs((prev) => {
          if (prev.some((j) => j.id === newJob.id)) return prev
          return [...prev, newJob]
        })
      },
      job_opportunity_updated: (message: { data: unknown }) => {
        const updated = message.data as JobOpportunity
        setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)))
      },
      job_opportunity_deleted: (message: { data: unknown }) => {
        const data = message.data as { id: string }
        setJobs((prev) => prev.filter((j) => j.id !== data.id))
      },
      // Moving a job into Interviewing makes the backend create the interview
      // behind it, so keep the local interview set fresh — otherwise the stage
      // column would sit empty until the next page load.
      interview_created: (message: { data: unknown }) => {
        const created = message.data as Interview
        setInterviews((prev) => (prev.some((i) => i.id === created.id) ? prev : [...prev, created]))
      },
      interview_updated: (message: { data: unknown }) => {
        const updated = message.data as Interview
        setInterviews((prev) => prev.map((i) => (i.id === updated.id ? updated : i)))
      },
      // A bulk import announces only a count, so reload the list to show what it added.
      job_opportunities_imported: () => reloadRef.current(),
      ws_reconnected: () => reloadRef.current(),
    }),
    [setJobs]
  )

  useWebSocket(wsHandlers)

  useEffect(() => {
    loadJobs()
    loadClients()
    loadInterviews()
  }, [])

  // Interviews are org-wide here (the same `all: true` read the interview
  // pipeline used) so a deep link resolves regardless of which client the
  // header switcher happens to be on. Failure is non-fatal for the jobs table:
  // the stage column falls back to an em dash and the error is surfaced as a
  // toast rather than replacing the page-level jobs error.
  const loadInterviews = async () => {
    setInterviewsLoading(true)
    try {
      const result = await interviewsApi.list({ all: true })
      setInterviews(Array.isArray(result) ? result : [])
    } catch (err) {
      console.error('Failed to load interviews:', err)
      setInterviews([])
      showError('Could not load interview details. Interview stages may be missing.')
    } finally {
      setInterviewsLoading(false)
    }
  }

  // Used to build the client switcher + scope "My Clients" to the rep's assigned set.
  // Failure is non-fatal: the switcher just falls back to the full client list.
  const loadClients = async () => {
    const haveList = clients.length > 0
    if (!haveList) setClientsLoading(true)
    setClientsError(null)
    try {
      const result = await clientsApi.list({ all: true })
      setClients(Array.isArray(result) ? result : [])
    } catch (err) {
      console.error('Failed to load clients for the client switcher:', err)
      // Tracked separately from an empty list because the market-ready gate
      // below cannot tell them apart: with no clients loaded, the selected
      // client looks not-market-ready, and the page would tell the user their
      // client is not on the market when the truth is the request failed.
      // A list already in hand survives a failed refresh — emptying a picker
      // that worked a moment ago would lose more than it protects.
      if (!haveList) setClients([])
      setClientsError('Could not load clients. Please try again.')
    } finally {
      setClientsLoading(false)
    }
  }

  const loadJobs = async () => {
    setLoading(true)
    // The error is cleared only by a read that succeeds, so a retry in flight
    // keeps the failure on screen instead of flashing "No applications yet".
    try {
      const result = await jobOpportunitiesApi.list({ all: true })
      if (!Array.isArray(result)) {
        console.error('Unexpected response shape from list-job-opportunities:', result)
        setError('list-job-opportunities returned an unexpected response shape')
        showError('Failed to load job applications — unexpected response from server')
        setJobs([])
        return
      }
      setJobs(result)
      setError(null)
    } catch (err) {
      console.error('Failed to load job applications:', err)
      setError(`list-job-opportunities: ${err instanceof Error ? err.message : String(err)}`)
      setJobs([])
    } finally {
      setLoading(false)
      setFirstJobsReadDone(true)
    }
  }

  // MWA-362 — opening a job pushes ?job= so Back closes the dialog instead of
  // leaving the page. The state marks the entry as ours, which is what lets the
  // close button step back rather than stack a second copy of the board.
  const handleJobClick = (job: JobOpportunity) => {
    setSelectedJob(job)
    setDetailDialogOpen(true)
    const next = new URLSearchParams(searchParams)
    next.set('job', job.id)
    setSearchParams(next, { state: { jobOpenedHere: true } })
  }

  const handleJobUpdate = (updated: JobOpportunity) => {
    setJobs(jobs.map((j) => (j.id === updated.id ? updated : j)))
    setSelectedJob(updated)
  }

  const handleJobDelete = (id: string) => {
    setJobs(jobs.filter((j) => j.id !== id))
  }

  // Opens the interview behind a job without leaving the table. The loaded set
  // can legitimately not hold it yet — a job that was just moved into
  // Interviewing has its interview created server-side after this page's read,
  // and the WebSocket event can be missed — so fall back to fetching by id.
  const openInterview = async (interviewId: string): Promise<boolean> => {
    const known = interviews.find((i) => i.id === interviewId)
    if (known) {
      setSelectedInterview(known)
      setInterviewDialogOpen(true)
      return true
    }
    try {
      const fresh = await interviewsApi.get(interviewId)
      setInterviews((prev) =>
        prev.some((i) => i.id === fresh.id)
          ? prev.map((i) => (i.id === fresh.id ? fresh : i))
          : [...prev, fresh]
      )
      setSelectedInterview(fresh)
      setInterviewDialogOpen(true)
      return true
    } catch (err) {
      console.error('Failed to load interview:', err)
      showError('Interview not found')
      return false
    }
  }

  const handleInterviewUpdate = (updated: Interview) => {
    setInterviews((prev) => prev.map((i) => (i.id === updated.id ? updated : i)))
    setSelectedInterview(updated)
  }

  // Closing keeps the interview selected so the dialog stays mounted — its
  // WebSocket subscription and the guide-generation fallback timer live inside
  // it, and an in-flight generation must survive the dialog being dismissed.
  const handleInterviewDialogClose = () => {
    setInterviewDialogOpen(false)
    if (searchParams.get('interviewId')) {
      const next = new URLSearchParams(searchParams)
      next.delete('interviewId')
      setSearchParams(next, { replace: true })
    }
  }

  // Deep-link support: arriving with ?interviewId=<id> opens that interview's
  // dialog here. Relocated from the retired interview pipeline page, which is
  // where the link used to land. Fires once, when the initial interview read
  // settles; openInterview reports a miss as "Interview not found".
  useEffect(() => {
    if (!requestedInterviewId || interviewsLoading) return
    openInterview(requestedInterviewId).then((opened) => {
      // Drop the param on a miss so the dead link isn't retried on reload; on a
      // hit it stays until the dialog is closed, mirroring the retired page.
      if (!opened) {
        const next = new URLSearchParams(searchParams)
        next.delete('interviewId')
        setSearchParams(next, { replace: true })
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedInterviewId, interviewsLoading])

  // MWA-361 — same shape as the interview link above. Waits for this page's own
  // first read, because the jobs atom can still hold another page's list. The
  // tab follows the job so the row behind the dialog is the one it opened.
  // MWA-362 — the param going away (Back) closes the dialog.
  useEffect(() => {
    if (!requestedJobId) {
      setDetailDialogOpen(false)
      return
    }
    if (!firstJobsReadDone) return
    const job = jobs.find((j) => j.id === requestedJobId)
    if (job) {
      const tab = jobTabKey(job)
      if (tab !== activeTab) setActiveTab(tab, { replace: true })
      setSelectedJob(job)
      setDetailDialogOpen(true)
      return
    }
    showError('That opportunity could not be found')
    const next = new URLSearchParams(searchParams)
    next.delete('job')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedJobId, firstJobsReadDone])

  const handleJobDialogClose = () => {
    setDetailDialogOpen(false)
    if (!searchParams.get('job')) return
    // Opened from this board: undo that entry, so Back afterwards leaves the
    // page. Arrived by link: there is nothing of ours to undo, so just drop it.
    if ((location.state as { jobOpenedHere?: boolean } | null)?.jobOpenedHere) {
      navigate(-1)
      return
    }
    const next = new URLSearchParams(searchParams)
    next.delete('job')
    setSearchParams(next, { replace: true })
  }

  const handleAddJob = async (data: Parameters<typeof jobOpportunitiesApi.create>[0]) => {
    try {
      const newJob = await jobOpportunitiesApi.create(data)
      setJobs([...jobs, newJob])
      showSuccess(
        newJob.hasDuplicate
          ? 'Application created (note: a similar one already exists)'
          : 'Job application added successfully'
      )
    } catch (err) {
      console.error('Failed to create job application:', err)
      // Surface the reason the server gave (e.g. the 400 explaining that a
      // status needing an interview cannot be set at creation) rather than a
      // fixed string that throws the explanation away. Same pattern as
      // JobDetailDialog's save handler.
      showError(err instanceof Error ? err.message : 'Failed to create job application')
      throw err
    }
  }

  // MWA-224/252 — clientIds the logged-in rep is assigned to (coach / clientSuccess /
  // assignedTo). Drives the "My Clients" filter on the switcher.
  const assignedClientIds = useMemo(() => {
    const email = user?.email
    if (!email) return new Set<string>()
    return new Set(
      clients
        .filter((c) => c.coach === email || c.clientSuccess === email || c.assignedTo === email)
        .map((c) => c.id)
    )
  }, [clients, user?.email])

  // MWA-252 — client switcher options. The Job Search flow is a market-ready
  // activity (matches the Add picker's MWA-224 filter).
  //
  // MWA-338 — every market-ready client is selectable here, whatever the role.
  // The board defaults to the clients the user is responsible for, so the
  // picker is the deliberate way to look at someone else's, and narrowing it
  // would make that impossible. Options are sorted mine-first and grouped into
  // "My Clients" / "Other Clients" (MUI groups consecutive same-group options).
  // This is Job Applications only: Networking still narrows non-managers.
  const clientOptions = useMemo(() => {
    const eligible = clients.filter(isJobSearchEligible)
    return [...eligible].sort((a, b) => {
      const aMine = assignedClientIds.has(a.id) ? 0 : 1
      const bMine = assignedClientIds.has(b.id) ? 0 : 1
      if (aMine !== bMine) return aMine - bMine
      return (a.name || a.email || '').localeCompare(b.name || b.email || '')
    })
  }, [clients, assignedClientIds])

  const selectedClient =
    clientOptions.find((c) => c.id === selectedClientId) ||
    clients.find((c) => c.id === selectedClientId) ||
    null

  const isSelectedClientEligible = isJobSearchEligible(selectedClient)
  // Eligible-to-READ is not eligible-to-CREATE: a completed engagement is
  // readable but must not gain new applications.
  const isSelectedClientOnMarket = selectedClient?.workflowStage === 'market_ready'

  // Remember this client as the last one the Job Search flow opened, so the
  // flat /opportunities and /networking links (and the sidebar) know where
  // to send you next time. Only remember it once it is confirmed market-ready
  // — a client this page is about to block isn't a legitimate "last opened".
  useEffect(() => {
    if (selectedClientId && isSelectedClientEligible) {
      setRememberedClientId(selectedClientId)
    }
  }, [selectedClientId, isSelectedClientEligible, setRememberedClientId])

  // MWA-237 / MWA-297 — the selected client's contacts (warm-contact indicators)
  // and every interaction in one request (the job dialog's last-spoke lines).
  const boardNetwork = useClientNetwork(selectedClientId)
  const { contacts } = boardNetwork
  // MWA-358 — the all-clients board has no single client to load, so the job
  // dialog loads the network of the client whose opportunity is open. Keyed on
  // the selected job rather than on the dialog being open, so closing it does
  // not blank the section mid-fade and reopening the same client costs nothing.
  const dialogNetwork = useClientNetwork(selectedClientId ? null : selectedJob?.clientId ?? null)
  const jobDialogNetwork = selectedClientId ? boardNetwork : dialogNetwork

  // MWA-252 / MWA-338 — what feeds the tabs. With a client in the route it is
  // that client's applications. Without one this is the all-clients board, and
  // it is every application belonging to the clients you are responsible for.
  //
  // loadJobs() already reads every job (`all: true`), so neither case fetches
  // anything extra — this is the only thing that decides the scope.
  //
  // Nobody assigned to anything falls back to every eligible client rather than
  // an empty board, matching scopeJobSearchClients: a rep whose clients were
  // never assigned properly must not be shown nothing at all.
  const scopedJobs = useMemo(() => {
    if (selectedClientId) return jobs.filter((j) => j.clientId === selectedClientId)

    const mine = clientOptions.filter((c) => assignedClientIds.has(c.id))
    const scope = new Set((mine.length > 0 ? mine : clientOptions).map((c) => c.id))
    return jobs.filter((j) => scope.has(j.clientId))
  }, [jobs, selectedClientId, clientOptions, assignedClientIds])

  /** The all-clients board: no client in the route. */
  const isAllClients = !selectedClientId

  // MWA-308 — Search Firm outreach is an executive-only service, so the tab is
  // gated on the client's tier. Staff keep a disabled tab saying why; the
  // client portal hides it outright.
  const canSeeExecSearch = isExecutiveClient(selectedClient)

  // Why the tab is disabled, or null when it is usable. Two reasons can apply;
  // "pick a client" comes first because you cannot judge a tier without one.
  const execSearchHint = isAllClients
    ? EXEC_SEARCH_HINT
    : canSeeExecSearch
      ? null
      : EXEC_TIER_HINT

  // MWA-357 — Wild Card Outreach is switched on per client, independent of the
  // tier. Same treatment as Search Firm: a disabled tab that says why.
  const canSeeWildcard = hasWildcardOutreach(selectedClient)
  const wildcardHint = isAllClients ? WILDCARD_SELECT_HINT : canSeeWildcard ? null : WILDCARD_HINT

  // Clearing the client while sitting on a per-client tab would otherwise leave
  // that tab selected with nothing to show it. The same applies to switching to
  // a client who does not have Executive Search or Wild Card Outreach.
  useEffect(() => {
    if (isAllClients && isEntityTab(activeTab)) {
      setActiveTab(DEFAULT_TAB, { replace: true })
      return
    }
    // MWA-362 — ?tab= can arrive before the client it depends on has loaded;
    // judging it against no client would throw a valid tab away on reload.
    if (!selectedClient) return
    if (activeTab === 'execsearch' && !canSeeExecSearch) {
      setActiveTab(DEFAULT_TAB, { replace: true })
    }
    if (activeTab === 'wildcard' && !canSeeWildcard) {
      setActiveTab(DEFAULT_TAB, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllClients, activeTab, canSeeExecSearch, canSeeWildcard, selectedClient])

  // Group the scoped jobs into the fixed tabs (each job in exactly one tab).
  const jobsByTab = useMemo(() => {
    const grouped: Record<TabKey, JobOpportunity[]> = {
      hidden_opportunity: [],
      pending_decision: [],
      applied: [],
      following_up: [],
      interviewing: [],
      got_the_job: [],
      rejected: [],
      closed_not_accepting: [],
      archived: [],
    }
    for (const j of scopedJobs) grouped[jobTabKey(j)].push(j)
    for (const key of Object.keys(grouped) as TabKey[]) {
      grouped[key].sort(
        (a, b) => new Date(b.lastActivityDate || b.updatedAt).getTime() - new Date(a.lastActivityDate || a.updatedAt).getTime()
      )
    }
    return grouped
  }, [scopedJobs])

  const rows: JobOpportunity[] = isEntityTab(activeTab) ? [] : jobsByTab[activeTab]

  // ── CrowCase-style sort + filter for the jobs board (applies within the active tab) ──
  const theme = useTheme()
  const tableStyles = getEnhancedTableStyles(theme)

  // The board's three shapes. 1200 and 768 are the widths DESIGN.md names, so
  // they are written literally rather than borrowed from an MUI breakpoint
  // that happens to sit nearby — lg is 1200 today but that is a coincidence.
  const isPhone = useMediaQuery(theme.breakpoints.down(768))
  const isCompact = useMediaQuery(theme.breakpoints.down(1200))
  // Which row the tablet layout has opened to show the columns it dropped.
  // Reset whenever the tab changes, so an expander never reopens against a
  // different application that happens to sit in the same position.
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null)
  useEffect(() => {
    setExpandedRowId(null)
  }, [activeTab])
  // Below 1200 the table lays out from fixed column widths so it cannot outgrow
  // the space beside the rail; the columns it drops live in the row expander.
  // From 1200 the Interviewing tab's eight columns need more than 1200-1279
  // leaves at desktop padding, so the padding tightens across that range.
  const tableLayoutSx = isCompact ? getCompactTableSx(theme) : getTightDesktopTableSx(theme)
  const compactWidth = (width: number | string | Record<string, number | string>) =>
    isCompact ? { width } : undefined
  // On the Interviewing tab the stage is what the board is opened to see, so at
  // tablet widths it takes the decision's column and the decision moves into
  // the expander instead.
  const stageReplacesDecision = isCompact && activeTab === 'interviewing'

  const [orderBy, setOrderBy] = useState<string>('')
  const [order, setOrder] = useState<SortOrder>('asc')
  const [jobFilterValues, setJobFilterValues] = useState<{
    companyTier: string[]
    decision: string[]
    company: string | null
  }>({
    companyTier: [],
    decision: [],
    company: null,
  })

  const handleSort = (columnId: string) => {
    if (orderBy === columnId) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setOrderBy(columnId)
      setOrder('asc')
    }
  }

  // Company typeahead options are scoped to the selected client's jobs (across all
  // tabs) — an org-wide company list would be irrelevant and enormous.
  const companyOptions = useMemo(() => {
    const companies = new Set<string>()
    for (const j of scopedJobs) {
      if (j.company) companies.add(j.company)
    }
    return Array.from(companies).sort((a, b) => a.localeCompare(b))
  }, [scopedJobs])

  const jobFilters: FilterBarFilter[] = [
    {
      name: 'companyTier',
      label: 'Company Tier',
      options: COMPANY_TIERS.map((c) => ({ value: c.value, label: c.label })),
      value: jobFilterValues.companyTier,
    },
    {
      name: 'decision',
      label: 'Client Decision',
      options: [
        ...APPLY_DECISIONS.map((d) => ({ value: d.value, label: d.label })),
        { value: 'pending', label: 'Pending client' },
      ],
      value: jobFilterValues.decision,
    },
    {
      name: 'company',
      label: 'Company',
      type: 'autocomplete',
      options: companyOptions,
      value: jobFilterValues.company,
      getOptionLabel: (o: string) => o,
    },
  ]

  const handleJobFilterChange = (name: string, value: string[] | string | null) =>
    setJobFilterValues((prev) => ({ ...prev, [name]: value }))
  const clearJobFilters = () => setJobFilterValues({ companyTier: [], decision: [], company: null })
  const hasActiveJobFilters =
    jobFilterValues.companyTier.length > 0 || jobFilterValues.decision.length > 0 || !!jobFilterValues.company

  // Mirror the on-screen Client-Decision rendering so the column sorts by what's shown.
  const decisionSortLabel = (j: JobOpportunity) =>
    j.applyDecision
      ? APPLY_DECISION_LABELS[j.applyDecision] || j.applyDecision
      : j.decisionRequested
        ? 'Pending client'
        : ''

  // Shared by displayRows and jobCountsByTab, so the tab badges and the
  // visible rows always agree on what "filtered" means.
  const jobMatchesFilters = (j: JobOpportunity) => {
    if (jobFilterValues.companyTier.length > 0) {
      if (!jobFilterValues.companyTier.includes(resolveCompanyTier(j.companyTier))) return false
    }
    if (jobFilterValues.decision.length > 0) {
      const match = jobFilterValues.decision.some((d) =>
        d === 'pending' ? !!j.decisionRequested && !j.applyDecision : j.applyDecision === d
      )
      if (!match) return false
    }
    if (jobFilterValues.company && j.company !== jobFilterValues.company) return false
    return true
  }

  // Tab badge counts reflect the active companyTier/decision/company filters,
  // matching NetworkingContactsTable's category tabs, instead of always
  // showing the unfiltered per-client total.
  const jobCountsByTab = useMemo(() => {
    const grouped: Record<TabKey, number> = {
      hidden_opportunity: 0,
      pending_decision: 0,
      applied: 0,
      following_up: 0,
      interviewing: 0,
      got_the_job: 0,
      rejected: 0,
      closed_not_accepting: 0,
      archived: 0,
    }
    for (const j of scopedJobs) {
      if (jobMatchesFilters(j)) grouped[jobTabKey(j)]++
    }
    return grouped
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedJobs, jobFilterValues])

  const displayRows = useMemo(() => {
    const filtered = rows.filter(jobMatchesFilters)

    if (!orderBy) return filtered

    return filtered.slice().sort((a, b) => {
      let aVal: string | number
      let bVal: string | number
      switch (orderBy) {
        case 'client':
          aVal = a.clientName || ''
          bVal = b.clientName || ''
          break
        case 'company':
          aVal = a.company || ''
          bVal = b.company || ''
          break
        case 'jobTitle':
          aVal = a.jobTitle || ''
          bVal = b.jobTitle || ''
          break
        case 'fit':
          aVal = a.fitScore ?? -1
          bVal = b.fitScore ?? -1
          break
        case 'decision':
          aVal = decisionSortLabel(a)
          bVal = decisionSortLabel(b)
          break
        case 'location':
          aVal = a.location || ''
          bVal = b.location || ''
          break
        case 'applied':
          aVal = a.applicationDate ? new Date(a.applicationDate).getTime() : 0
          bVal = b.applicationDate ? new Date(b.applicationDate).getTime() : 0
          break
        default:
          return 0
      }
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return order === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, orderBy, order, jobFilterValues])

  // MWA-254 Defect 2 — client-side pagination. resetKey jumps back to page 1
  // when the active tab, sort, or filters change.
  const { paginated: pagedRows, ...pageCtl } = useTablePagination(
    displayRows,
    `${activeTab}|${orderBy}|${order}|${jobFilterValues.companyTier.join(',')}|${jobFilterValues.decision.join(',')}|${jobFilterValues.company || ''}`
  )

  const formatPosted = (dateStr?: string) =>
    formatJobDate(dateStr, 'en-US', { month: 'short', day: 'numeric' })

  // An interview is a child of a job opportunity: the parent's
  // linkedInterviewId is the single direction of truth.
  const interviewById = useMemo(() => {
    const map: Record<string, Interview> = {}
    for (const i of interviews) map[i.id] = i
    return map
  }, [interviews])

  // Interview stages are admin-configured rows in the interviewStageDefinitions
  // table (Stage Gates), so this map can legitimately be empty — there is no
  // hardcoded fallback set and none should be introduced.
  const interviewStageById = useMemo(() => {
    const map: Record<string, InterviewStageDefinition> = {}
    for (const s of interviewStages) map[s.stageId] = s
    return map
  }, [interviewStages])

  // Interview stage cell (Interviewing tab). Reads the interview record's own
  // status and labels it from the stage definitions; with no definition to match
  // — an empty or reconfigured stage table — it still shows the raw stage rather
  // than rendering blank. The chip is the row-level way into the interview, so
  // it opens the interview dialog here without leaving the table.
  const renderInterviewStage = (j: JobOpportunity) => {
    const emDash = (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    )
    if (!j.linkedInterviewId) return emDash
    const interview = interviewById[j.linkedInterviewId]
    if (!interview) return interviewsLoading ? <Skeleton variant="text" width={90} /> : emDash
    const stage = interviewStageById[interview.status]
    // Stage definitions arrive from their own read. Labelling the chip from the
    // raw status while that read is in flight puts developer vocabulary ("prep
    // guide sent") in front of staff for a beat, so wait for the labels.
    if (!stage && interviewStagesLoading) return <Skeleton variant="text" width={90} />
    const label = stage?.label || interview.status.replace(/_/g, ' ')
    return (
      <Tooltip title="Open interview prep">
        <Chip
          label={label}
          size="small"
          aria-label={`Open interview prep — ${label}`}
          variant={stage ? 'filled' : 'outlined'}
          sx={stage ? { backgroundColor: stage.color, color: 'white', fontWeight: 500 } : undefined}
          onClick={(e) => {
            // The row itself opens the job, so keep the two apart.
            e.stopPropagation()
            openInterview(interview.id)
          }}
        />
      </Tooltip>
    )
  }

  const renderCompanyTier = (j: JobOpportunity) => (
    <Chip
      label={COMPANY_TIER_LABELS[resolveCompanyTier(j.companyTier)]}
      size="small"
      color={COMPANY_TIER_CHIP_COLOR[resolveCompanyTier(j.companyTier)]}
      sx={{ height: 18, typography: 'badge' }}
    />
  )

  const renderDecision = (j: JobOpportunity) => {
    if (j.applyDecision) {
      return (
        <Chip
          label={APPLY_DECISION_LABELS[j.applyDecision] || j.applyDecision}
          size="small"
          sx={{ backgroundColor: APPLY_DECISION_COLORS[j.applyDecision], color: 'white', fontWeight: 500 }}
        />
      )
    }
    if (j.decisionRequested) {
      return <Chip label="Pending client" size="small" color="warning" variant="outlined" />
    }
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    )
  }

  // MWA-253/254 — single Add menu in the header.
  const openAddMenu = (e: React.MouseEvent<HTMLElement>) => setAddMenuAnchor(e.currentTarget)
  const closeAddMenu = () => setAddMenuAnchor(null)

  // A board-shaped skeleton rather than a centred spinner. The header, tab
  // strip and table frame hold their place while the first read lands, so
  // nothing jumps when it does and the reader can already see what is coming.
  //
  // Holding its place means every block below is the height of the block it
  // stands in for, in the same containers with the same margins: the header
  // wraps where the real one wraps because it carries the real title and
  // controls of the real widths, and the filter bar is the real one, since its
  // labels and enums need no data. Rows are the board's own single-line
  // height: 51px, set by the 24px chips, or 57px below 1200 where the 30px
  // expander button sets it.
  if (((loading && !firstJobsReadDone) || clientsLoading) && jobs.length === 0) {
    const skeletonColumns = isCompact ? 6 : 7
    return (
      <Box sx={{ height: { xs: 'calc(100vh - 100px)', sm: 'calc(100vh - 120px)' }, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1, sm: 2 }, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', minWidth: 0 }}>
            <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              Front Door
            </Typography>
            <PageHelpButton articleId="page-job-applications" />
            <Skeleton variant="rounded" width={260} height={37} />
          </Box>
          <Skeleton variant="rounded" width={105} height={35} />
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ height: 48, borderBottom: 1, borderColor: 'divider', mb: 1, px: 2, overflow: 'hidden', flexShrink: 0 }}
        >
          {[104, 148, 72, 112, 96].map((w, i) => (
            <Skeleton key={i} variant="text" width={w} height={20} sx={{ flexShrink: 0 }} />
          ))}
        </Stack>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <FilterBar filters={jobFilters} onChange={handleJobFilterChange} onClearAll={clearJobFilters} />
          {isPhone ? (
            <Stack spacing={1}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} variant="rounded" height={132} sx={{ borderRadius: '10px' }} />
              ))}
            </Stack>
          ) : (
            <Paper variant="outlined" sx={tableStyles.tableContainer}>
              <Table sx={tableStyles.table}>
                <TableHead>
                  <TableRow>
                    {Array.from({ length: skeletonColumns }, (_, i) => (
                      <TableCell key={i}>
                        <Skeleton variant="text" width={i === 1 ? 72 : 48} height={14} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableSkeleton rows={9} columns={skeletonColumns} rowHeight={isCompact ? 57 : 51} />
                </TableBody>
              </Table>
            </Paper>
          )}
        </Box>
      </Box>
    )
  }

  // A failed client load leaves the selected client looking not-market-ready,
  // so it has to be ruled out before the gate below, or the page blames the
  // client's stage for a network failure.
  if (!clientsLoading && clientsError) {
    return (
      <Box sx={{ maxWidth: 520, mx: 'auto', mt: 8 }}>
        <ErrorState
          title="Couldn't load the client list"
          description="The board needs it to know which client this is. Nothing was changed, and retrying is safe."
          detail={clientsError}
          onRetry={loadClients}
        />
      </Box>
    )
  }

  // Job Search is a market-ready activity — a direct/bookmarked URL for any
  // other client shows this instead of the tracker (never the tracker itself,
  // never a silent redirect to a different client).
  if (!clientsLoading && selectedClientId && !isSelectedClientEligible) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 8, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          This client isn&rsquo;t on the market yet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Front Door opens once a client reaches the Market Ready stage.
        </Typography>
        <Button component={RouterLink} to={`/clients/${selectedClientId}`} variant="outlined">
          View client record
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ height: { xs: 'calc(100vh - 100px)', sm: 'calc(100vh - 120px)' }, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1, sm: 2 }, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', minWidth: 0 }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Front Door
          </Typography>
          <PageHelpButton articleId="page-job-applications" />
          {/* MWA-252 — client switcher scopes the whole flow to one client.
              The list is grouped into "My Clients" / "Other Clients" (replacing
              the old Mine/All toggle) so client ownership is shown inline,
              right where you pick. MWA-338 made that grouping unconditional
              here, because every role can now reach any client's board.

              Sits beside the title, and is what names the client on screen — the
              top nav is navigation alone now. A separate name label next to the
              title only repeated what this field already reads.

              The only client selector on this page at every width, since the
              toolbar no longer carries one. */}
          <ClientPicker
            options={clientOptions}
            value={selectedClient}
            onChange={(c) => {
              // Clearing now has somewhere to land: the all-clients board.
              navigate(c ? `/clients/${c.id}/opportunities` : '/opportunities')
            }}
            isManager={isManager}
            assignedClientIds={assignedClientIds}
            groupOptions
            placeholder="All my clients"
            // Going back to the all-clients board has to be something you can
            // choose from the list, not only the clear button.
            clearOptionLabel="All my clients"
            // MWA-338 — the page-level spinner only covers a cold read
            // (jobs.length === 0), so arriving with jobs already in the atom
            // paints this picker while clients are still loading: no options to
            // click, and no name for a board the route has already scoped.
            // Saying "loading" is the difference between a slow field and a
            // broken one.
            loading={clientsLoading}
          />
        </Box>
        {/* Everything Add creates belongs to one client, so it waits for one. */}
        <Tooltip title={isAllClients ? 'Select a client to add an application' : ''}>
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={openAddMenu}
              disabled={isAllClients}
            >
              Add
            </Button>
          </span>
        </Tooltip>
        <Menu anchorEl={addMenuAnchor} open={!!addMenuAnchor} onClose={closeAddMenu}>
          {/* The two creation paths are offered only while the client is still
              on the market. This page also opens for clients at 'complete' so
              staff can read a finished engagement's history, and starting a new
              application against a closed engagement is not the same thing.

              The gate has to live here, not in AddJobDialog: this page passes
              the dialog an initialClient, and in that locked mode the dialog
              never consults its own market-ready option list, so its filter
              does not apply. */}
          <MenuItem
            disabled={!isSelectedClientOnMarket}
            onClick={() => {
              closeAddMenu()
              setAddDialogOpen(true)
            }}
          >
            <ListItemIcon><BusinessCenterIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Add Opportunity"
              secondary={isSelectedClientOnMarket ? undefined : 'Client is no longer on the market'}
            />
          </MenuItem>
          {/* Row 11 — one-time backfill of a client's existing application history.
              Needs a client selected, same as the single-add path. */}
          <MenuItem
            disabled={!selectedClientId || !isSelectedClientOnMarket}
            onClick={() => {
              closeAddMenu()
              setImportDialogOpen(true)
            }}
          >
            <ListItemIcon><UploadFileIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Import Applications</ListItemText>
          </MenuItem>
          {/* MWA-304 — gated the same way as the other creation paths: this
              page also opens for finished engagements so staff can read the
              history, and logging a new opportunity against one is not that. */}
          <MenuItem
            disabled={!selectedClientId || !isSelectedClientOnMarket}
            onClick={() => {
              closeAddMenu()
              setLogHiddenOpen(true)
            }}
          >
            <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Log Hidden Opportunity"
              secondary={
                !selectedClientId
                  ? 'Select a client first'
                  : isSelectedClientOnMarket
                    ? undefined
                    : 'Client is no longer on the market'
              }
            />
          </MenuItem>
          <MenuItem
            onClick={() => {
              closeAddMenu()
              setActiveTab('targetcompanies')
              // Only arm the trigger when a client is selected — otherwise the tab
              // renders the "select a client" state instead of mounting, leaving a
              // stale flag that would pop the dialog on a later visit.
              if (selectedClientId) setTcAutoAdd(true)
            }}
          >
            <ListItemIcon><DomainIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Add Target Company</ListItemText>
          </MenuItem>
          {/* MWA-308 — this jumps to the Executive Search tab, which is gated on
              the client's tier. Without the same gate the item stayed clickable
              for a standard client and did nothing at all: the tab bounced it
              straight back, with no dialog and no explanation. */}
          <MenuItem
            disabled={!!execSearchHint}
            onClick={() => {
              closeAddMenu()
              setActiveTab('execsearch')
              if (selectedClientId) setEsAutoAdd(true)
            }}
          >
            <ListItemIcon><PersonSearchIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Add Recruiter" secondary={execSearchHint ?? undefined} />
          </MenuItem>
          <MenuItem
            disabled={!!wildcardHint}
            onClick={() => {
              closeAddMenu()
              setActiveTab('wildcard')
              if (selectedClientId) setWcAutoAdd(true)
            }}
          >
            <ListItemIcon><TravelExploreIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Add Wild Card Entry" secondary={wildcardHint ?? undefined} />
          </MenuItem>
        </Menu>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v as TabKey | EntityTab)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
      >
        {/* MWA-254 — Target Companies + Executive Search sit after the pipeline tabs. */}
        {TABS.map((t) => (
          <Tab
            key={t.key}
            value={t.key}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {t.label}
                <Chip label={jobCountsByTab[t.key]} size="small" sx={{ height: 18, typography: 'badge' }} />
              </Box>
            }
          />
        ))}
        {/* Both of these belong to one client — target companies come off that
            client's dream job, recruiters are engaged for that client — so on
            the all-clients board they say why they are unavailable rather than
            disappearing or, worse, rendering empty as if there were none.

            The reason has to survive two things a disabled tab does. MUI gives
            it `pointer-events: none`, so hover never lands and no tooltip can
            open — restored below, which costs nothing because `disabled` is what
            actually blocks selection. And it leaves the tab order, so a tooltip
            alone would leave keyboard and screen-reader users with a grey tab
            and no reason; the aria-label carries it for them.

            The Tooltip goes around the LABEL, not the Tab. Tabs clones its
            direct children to pass `value` and selection down, so wrapping the
            Tab makes it invisible to that and MUI falls back to positional
            values — selection silently breaks. */}
        <Tab
          value="targetcompanies"
          label={
            isAllClients ? (
              <Tooltip title={TARGET_COMPANIES_HINT}>
                <span>Target Companies</span>
              </Tooltip>
            ) : (
              'Target Companies'
            )
          }
          disabled={isAllClients}
          aria-label={isAllClients ? `Target Companies. ${TARGET_COMPANIES_HINT}` : undefined}
          sx={isAllClients ? { '&.Mui-disabled': { pointerEvents: 'auto' } } : undefined}
        />
        {/* MWA-253 — Executive Search recruiters. */}
        <Tab
          value="execsearch"
          label={
            execSearchHint ? (
              <Tooltip title={execSearchHint}>
                <span>Executive Search</span>
              </Tooltip>
            ) : (
              'Executive Search'
            )
          }
          disabled={!!execSearchHint}
          aria-label={execSearchHint ? `Executive Search. ${execSearchHint}` : undefined}
          sx={execSearchHint ? { '&.Mui-disabled': { pointerEvents: 'auto' } } : undefined}
        />
        {/* MWA-357 — Wild Card Outreach, beside Search Firm because it works
            the same way. Disabled with the reason, as above. */}
        <Tab
          value="wildcard"
          label={
            wildcardHint ? (
              <Tooltip title={wildcardHint}>
                <span>Wild Card Outreach</span>
              </Tooltip>
            ) : (
              'Wild Card Outreach'
            )
          }
          disabled={!!wildcardHint}
          aria-label={wildcardHint ? `Wild Card Outreach. ${wildcardHint}` : undefined}
          sx={wildcardHint ? { '&.Mui-disabled': { pointerEvents: 'auto' } } : undefined}
        />
      </Tabs>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {isAllClients && clientOptions.length === 0 ? (
          <EmptyState
            icon={<BusinessCenterIcon />}
            title="No market-ready clients"
            description="Front Door opens for a client once they reach the Market Ready stage. Nobody has yet."
          />
        ) : error && !isEntityTab(activeTab) ? (
          // Every tab but the entity ones is fed by the applications read, so a
          // failed read replaces them outright: an empty board under an error
          // would claim there is nothing to show.
          loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 7 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <ErrorState
              title="Couldn't load job applications"
              description="The board is showing nothing rather than part of the list. Nothing was changed, and retrying is safe."
              detail={error}
              onRetry={loadJobs}
            />
          )
        ) : activeTab === 'execsearch' ? (
          <ExecutiveSearchTab
            key="execsearch"
            clientId={selectedClientId ?? undefined}
            canEdit
            showAddButton={false}
            autoOpenAdd={esAutoAdd}
            onAutoAddHandled={() => setEsAutoAdd(false)}
          />
        ) : activeTab === 'wildcard' ? (
          <ExecutiveSearchTab
            // Keyed so switching between the two tabs remounts rather than
            // briefly showing one list's rows under the other's heading.
            key="wildcard"
            outreachType="wildcard"
            clientId={selectedClientId ?? undefined}
            canEdit
            showAddButton={false}
            autoOpenAdd={wcAutoAdd}
            onAutoAddHandled={() => setWcAutoAdd(false)}
          />
        ) : activeTab === 'hidden_opportunity' ? (
          <HiddenOpportunitiesTab
            jobs={jobsByTab.hidden_opportunity}
            contacts={contacts}
            onOpen={handleJobClick}
            emptyHint={
              isAllClients
                ? 'No hidden opportunities logged yet. Select a client to log one from +Add.'
                : 'No hidden opportunities logged for this client yet. When a public signal says a seat is opening, log it from +Add.'
            }
            showClientName={isAllClients}
            // MWA-355 — staff read the client's answer here, including the ones
            // the client declined, which have left the client's own view.
            audience="staff"
          />
        ) : activeTab === 'targetcompanies' ? (
          <TargetCompaniesTab
            clientId={selectedClientId ?? undefined}
            canEdit
            showAddButton={false}
            autoOpenAdd={tcAutoAdd}
            onAutoAddHandled={() => setTcAutoAdd(false)}
          />
        ) : (
          <>
            <FilterBar
              filters={jobFilters}
              onChange={handleJobFilterChange}
              onClearAll={clearJobFilters}
              resultCount={displayRows.length}
            />
            {/* MWA-281 — with nothing on the board at all, name the next step.
                Once other tabs hold applications, stay tab-specific so the
                message never reads as "add one" on Closed or Archived.

                Filtered-to-zero is a separate state from nothing-here-yet,
                because the fix is different: one wants an application added,
                the other wants its filters back. The FilterBar above stays
                mounted either way, so the cause of an empty board is on
                screen next to the explanation. */}
            {displayRows.length === 0 ? (
              scopedJobs.length === 0 ? (
                <EmptyState
                  icon={<BusinessCenterIcon />}
                  title="No applications yet"
                  description={
                    selectedClient
                      ? `Nothing is being tracked for ${selectedClient.name} yet. Add the first application, or import an existing history.`
                      : 'No client on this board has an application tracked yet. Choose a client above to add the first one.'
                  }
                  action={
                    isAllClients ? undefined : (
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        endIcon={<ArrowDropDownIcon />}
                        onClick={openAddMenu}
                      >
                        Add
                      </Button>
                    )
                  }
                />
              ) : hasActiveJobFilters ? (
                <EmptyState
                  icon={<FilterAltOutlinedIcon />}
                  title="No applications match these filters"
                  description={`${rows.length} ${rows.length === 1 ? 'application sits' : 'applications sit'} under ${TABS.find((t) => t.key === activeTab)?.label}. The filters above are hiding all of them.`}
                  action={
                    <Button variant="outlined" onClick={clearJobFilters}>
                      Clear filters
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={<BusinessCenterIcon />}
                  title={`Nothing in ${TABS.find((t) => t.key === activeTab)?.label}`}
                  description={emptyTabDescription(activeTab, 'Applications appear here as they reach this stage.')}
                />
              )
            ) : isPhone ? (
              /* Below 768px a seven-column board can only scroll sideways or
                 drop columns, and both cost data. The card carries every field
                 the row carries, so narrowing the window costs layout only. */
              <>
                <Stack spacing={1} sx={{ pb: 1 }}>
                  {pagedRows.map((j) => (
                    <JobApplicationCard
                      key={j.id}
                      job={j}
                      contacts={contacts}
                      showClient={isAllClients}
                      showInactivity={!TERMINAL_JOB_TABS.includes(activeTab as TabKey)}
                      showWarmContacts={!isAllClients}
                      decision={j.applyDecision || j.decisionRequested ? renderDecision(j) : null}
                      interviewStage={activeTab === 'interviewing' ? renderInterviewStage(j) : null}
                      appliedLabel={formatPosted(j.applicationDate)}
                      onOpen={() => handleJobClick(j)}
                    />
                  ))}
                </Stack>
                <TablePaginationBar count={pageCtl.count} page={pageCtl.page} rowsPerPage={pageCtl.rowsPerPage} setPage={pageCtl.setPage} setRowsPerPage={pageCtl.setRowsPerPage} />
              </>
            ) : (
              <Paper variant="outlined" sx={tableStyles.tableContainer}>
                <Box sx={{ overflow: 'auto', maxHeight: '70vh' }}>
                  <Table stickyHeader sx={{ ...tableStyles.table, ...tableLayoutSx }}>
                    <TableHead>
                      <TableRow>
                        {/* Whose application this is only needs saying when the
                            board is showing more than one client's. Below 1200
                            it moves into the row expander with the rest. */}
                        {isAllClients && !isCompact && (
                          <SortableTableHeader id="client" label="Client" orderBy={orderBy} order={order} onSort={handleSort} />
                        )}
                        {/* Job Role has no width: it takes what the others leave. */}
                        <SortableTableHeader id="company" label="Company" orderBy={orderBy} order={order} onSort={handleSort} sx={compactWidth('20%')} />
                        <SortableTableHeader id="jobTitle" label="Job Role" orderBy={orderBy} order={order} onSort={handleSort} />
                        <SortableTableHeader id="fit" label="Fit" orderBy={orderBy} order={order} onSort={handleSort} sx={compactWidth({ xs: 44, md: 52 })} />
                        {!stageReplacesDecision && (
                          <SortableTableHeader
                            id="decision"
                            label="Client Decision"
                            orderBy={orderBy}
                            order={order}
                            onSort={handleSort}
                            sx={compactWidth({ xs: '23%', md: 156 })}
                          />
                        )}
                        {/* Location and Warm Contacts are what the tablet width
                            drops: the two widest columns, and the two least
                            likely to be why the board was opened. The expander
                            puts them one tap away, which beats a horizontal
                            scrollbar that hides them with no sign they exist. */}
                        {!isCompact && (
                          <SortableTableHeader id="location" label="Location" orderBy={orderBy} order={order} onSort={handleSort} />
                        )}
                        {/* Where each interview actually stands, on the tab
                            where that is the question being asked. Kept at
                            every width, because on this tab it is the answer. */}
                        {activeTab === 'interviewing' && (
                          <TableCell sx={compactWidth({ xs: '23%', md: 156 })}>Interview Stage</TableCell>
                        )}
                        {/* MWA-358 — contacts load for one client at a time, so
                            on the all-clients board this column could only
                            ever say "—", which reads as "nobody". */}
                        {!isCompact && !isAllClients && <TableCell>Warm Contacts</TableCell>}
                        {/* applicationDate = date applied / logged (no separate job-posted date yet). */}
                        <SortableTableHeader id="applied" label="Applied" orderBy={orderBy} order={order} onSort={handleSort} sx={compactWidth({ xs: 64, md: 84 })} />
                        {isCompact && (
                          <ExpanderHeaderCell />
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedRows.map((j) => (
                        <Fragment key={j.id}>
                  <TableRow hover onClick={() => handleJobClick(j)} sx={{ cursor: 'pointer' }}>
                    {isAllClients && !isCompact && (
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {j.clientName || '—'}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {j.company}
                        </Typography>
                        {!isCompact && renderCompanyTier(j)}
                        {/* MWA-281 — no staleness warning on the terminal tabs;
                            nothing is expected to move once an application has
                            reached an end state or the client passed on it. */}
                        {!TERMINAL_JOB_TABS.includes(activeTab as TabKey) && (
                          <InactivityChip job={j} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {j.url ? (
                        <Link
                          href={ensureHttpProtocol(j.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                        >
                          {j.jobTitle}
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </Link>
                      ) : (
                        <Typography variant="body2">{j.jobTitle}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {j.fitScore != null ? (
                        <Tooltip title={j.fitScoreReason || 'Job fit score'}>
                          <Chip
                            label={j.fitScore}
                            size="small"
                            sx={{ backgroundColor: fitScoreColor(j.fitScore), color: 'white', fontWeight: 700 }}
                          />
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    {!stageReplacesDecision && <TableCell>{renderDecision(j)}</TableCell>}
                    {!isCompact && (
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {j.location || '—'}
                        </Typography>
                      </TableCell>
                    )}
                    {activeTab === 'interviewing' && <TableCell>{renderInterviewStage(j)}</TableCell>}
                    {!isCompact && !isAllClients && (
                      <TableCell>
                        <WarmContactsCell
                          contacts={contacts}
                          company={j.company}
                          linkedContactIds={j.linkedContactIds}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatPosted(j.applicationDate)}
                      </Typography>
                    </TableCell>
                    {isCompact && (
                      <ExpanderToggleCell
                        open={expandedRowId === j.id}
                        onToggle={() => setExpandedRowId(expandedRowId === j.id ? null : j.id)}
                        subject={`${j.jobTitle} at ${j.company}`}
                      />
                    )}
                  </TableRow>
                  {/* The columns this width dropped, one tap away. */}
                  {isCompact && (
                    <ExpanderRow open={expandedRowId === j.id} colSpan={6}>
                      {isAllClients && (
                        <ExpanderField label="Client">
                          <Typography variant="body2">{j.clientName || '—'}</Typography>
                        </ExpanderField>
                      )}
                      <ExpanderField label="Location">
                        <Typography variant="body2">{j.location || '—'}</Typography>
                      </ExpanderField>
                      {!isAllClients && (
                        <ExpanderField label="Warm Contacts">
                          <WarmContactsCell contacts={contacts} company={j.company} linkedContactIds={j.linkedContactIds} />
                        </ExpanderField>
                      )}
                      <ExpanderField label="Company Tier">{renderCompanyTier(j)}</ExpanderField>
                      {stageReplacesDecision && <ExpanderField label="Client Decision">{renderDecision(j)}</ExpanderField>}
                    </ExpanderRow>
                  )}
                        </Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
                <TablePaginationBar count={pageCtl.count} page={pageCtl.page} rowsPerPage={pageCtl.rowsPerPage} setPage={pageCtl.setPage} setRowsPerPage={pageCtl.setRowsPerPage} />
              </Paper>
            )}
          </>
        )}
      </Box>

      <AddJobDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAddJob}
        initialClient={selectedClient}
      />

      {selectedClientId && (
        <LogHiddenOpportunityDialog
          open={logHiddenOpen}
          onClose={() => setLogHiddenOpen(false)}
          clientId={selectedClientId}
          clientName={selectedClient?.name || ''}
          onLog={handleAddJob}
        />
      )}

      {selectedClientId && (
        <ImportJobApplicationsDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          clientId={selectedClientId}
          clientName={selectedClient?.name}
          onImported={loadJobs}
        />
      )}

      <JobDetailDialog
        contacts={jobDialogNetwork.contacts}
        interactions={jobDialogNetwork.interactions}
        contactsLoading={jobDialogNetwork.loading}
        contactsError={jobDialogNetwork.error}
        onRetryContacts={jobDialogNetwork.retry}
        onContactUpdate={jobDialogNetwork.replaceContact}
        open={detailDialogOpen}
        job={selectedJob}
        onClose={handleJobDialogClose}
        onUpdate={handleJobUpdate}
        onDelete={handleJobDelete}
        onViewInterview={openInterview}
      />

      {/* Mounted unconditionally, exactly as the retired interview pipeline
          page mounted it. The dialog opens its own WebSocket subscription and
          owns the fallback timer that resolves an asynchronous guide
          generation, so it must not be unmounted by a client switch, a tab
          change, or the dialog being closed mid-generation. */}
      <InterviewDetailDialog
        open={interviewDialogOpen}
        interview={selectedInterview}
        onClose={handleInterviewDialogClose}
        onUpdate={handleInterviewUpdate}
      />
    </Box>
  )
}
